// Small d3-force helper that pulls each node toward the centroid of its
// cluster (an arbitrary grouping key the caller supplies). Used by
// useForceSimulation to keep related nodes visually grouped without hard-
// pinning members. The org graph uses `managerUpn ?? id` as the cluster key
// so manager+reports form pods, but any grouping is supported.
//
// Algorithm:
//   1. On every tick, compute the centroid (mean x, mean y) of nodes that
//      share a clusterId.
//   2. Apply a small velocity nudge toward that centroid, scaled by `strength`
//      and the timestep `alpha` so the force fades as the simulation cools.
//
// This is a pure factory: it returns a d3-force-compatible function with
// initialize/strength methods. Keep this file under ~40 LOC so it stays easy
// to reason about and below SonarCloud's S3776 cognitive-complexity threshold.
import type { SimulationNodeDatum } from 'd3-force';

export interface ClusterNode extends SimulationNodeDatum {
  clusterId: string;
}

export interface ClusterForce {
  (alpha: number): void;
  initialize: (nodes: ClusterNode[]) => void;
  strength: (value: number) => ClusterForce;
}

export function forceCluster(initialStrength = 0.1): ClusterForce {
  let nodes: ClusterNode[] = [];
  let strength = initialStrength;

  const force = ((alpha: number) => {
    const centroids = new Map<string, { x: number; y: number; n: number }>();
    for (const node of nodes) {
      const c = centroids.get(node.clusterId) ?? { x: 0, y: 0, n: 0 };
      c.x += node.x ?? 0;
      c.y += node.y ?? 0;
      c.n += 1;
      centroids.set(node.clusterId, c);
    }
    for (const node of nodes) {
      const c = centroids.get(node.clusterId);
      if (!c || c.n === 0) continue;
      const cx = c.x / c.n;
      const cy = c.y / c.n;
      node.vx = (node.vx ?? 0) + (cx - (node.x ?? 0)) * strength * alpha;
      node.vy = (node.vy ?? 0) + (cy - (node.y ?? 0)) * strength * alpha;
    }
  }) as ClusterForce;

  force.initialize = (input: ClusterNode[]) => {
    nodes = input;
  };
  force.strength = (value: number) => {
    strength = value;
    return force;
  };

  return force;
}
